import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { scoreText, type ScoreResult } from '../components/AddWordBox';
import { useUiStore } from '../stores/uiStore';
import { useBinderStore } from '../stores/binderStore';
import { trackRun, useRunStore, type Run } from '../stores/runStore';
import { RunTimer } from '../components/RunIndicator';
import Spinner from '../components/Spinner';
import ContentAuditView from './ContentAuditView';
import { mwToRespelling, nextPronunciationCheck } from '../utils/mwRespell';
import { draftChanged, saveDrafts, type DraftSave } from '../utils/directionDrafts';
import { newestFirst } from '../utils/fakeOrder';
import './ContentPage.css';

// Content tab, Gate 2 (Chunk W-Content, docs/word-engine-plan.md; staged
// pipeline corrections 2026-09-27). The Word Binder on the left lists the
// words; picking one opens its page here. A stage strip across the top
// (1 Meaning, 2 Fakes, 3 Reveals, 4 Assignment, 5 Examples, 6 Win/Lose since
// 2026-10-30; the Hook is retired and has no tab). Each stage has
// Generate, per-field Regenerate, inline edit and Approve stage; the next
// stage's Generate unlocks only when the one before it is approved. The sense
// defaults to the vetted definition; "change sense" opens the picker. Every
// call is one word, one stage. Nothing here runs in bulk and nothing reads the
// auto-chain switch. Every model call here goes through trackRun (2026-10-22):
// its button pulses with a timer, the stage strip and the Word Binder show a
// spinner, and the top bar keeps it visible on every tab until it ends.

type Status = 'none' | 'generated' | 'needs_fixes' | 'approved' | 'missing';

interface FieldRow {
  field: string;
  value: string | null;
  optional: boolean;
  // People Also Ask (2026-10-22): checked, never blocks approval.
  advisory?: boolean;
  check: { reason: string; blocking: boolean } | null;
}
type StageStatus = 'empty' | 'generated' | 'needs_fixes' | 'approved';
interface StagePanel {
  id: string;
  label: string;
  status: StageStatus;
  approved_at: string | null;
  generated_at: string | null;
  // The newest Generate or Regenerate in the stage (2026-10-24).
  last_generated_at?: string | null;
  can_generate: boolean;
  generate_blocked: string | null;
  fields: FieldRow[];
  candidates?: FakeCandidate[];
  // Fake judge (2026-10-22): its last verdict and who made the current picks.
  // odd_one_out (2026-10-24): the real definition stands out beside the judge's three. Never blocks approval.
  judge?: { top3: string[]; at: string; model: string | null; odd_one_out?: { flag: boolean; reason: string } | null } | null;
  picks_by?: 'odin' | 'judge' | 'auto' | null;
  // Reveal lines (2026-10-03): the picked fake each line sits under.
  picked_fakes?: string[];
}
// Stage 1b keeps every generated fake as a candidate; Odin picks three.
interface FakeCandidate {
  id: string;
  text: string;
  selected: boolean;
  source: string;
  created_at: string | null;
  // Set when the fake is regenerated (2026-10-24); its made time is this, else created_at.
  regenerated_at?: string | null;
  refused: string | null;
  note?: string | null;
  // The fake judge's score (1 to 10) and one-line reason (2026-10-22).
  judge?: { score: number; reason: string; at: string } | null;
}

/** "Oct 23, 4:12 PM" in Central time (2026-10-24). */
function centralTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/** What the judge did, for the message under the stage. */
interface JudgeResult { judge?: { top3: string[]; model: string | null; pre_ticked: boolean }; judge_error?: string }
function judgeNote(r: JudgeResult, label: (id: string) => string): string {
  if (r.judge_error) return r.judge_error;
  if (!r.judge) return '';
  const top = r.judge.top3.map(label).join(', ');
  return r.judge.pre_ticked
    ? `The judge picked ${top}. Change any pick before you approve.`
    : `The judge's top three: ${top}. Your picks were not changed.`;
}
interface WordCostCall {
  ts: string;
  model: string | null;
  input_tokens: number;
  cache_read_tokens: number | null;
  cache_write_tokens: number | null;
  output_tokens: number;
  usd: number;
}
interface WordStageCost {
  stage_id: string;
  // A call in this word's session made for another word (its Related Words).
  other_word?: boolean;
  calls: number;
  usd: number;
}
interface WordCost {
  by_stage?: WordStageCost[];
  calls: number;
  usd: number;
  input_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  recent: WordCostCall[];
}

/** "$0.0512 · 19,377 in (15,002 cached, 0 written) · 2,068 out". */
function costText(c: { usd: number; input_tokens: number; cache_read_tokens: number | null; cache_write_tokens: number | null; output_tokens: number }): string {
  const n = (x: number | null) => (x ?? 0).toLocaleString('en-US');
  return `$${c.usd.toFixed(4)} · ${n(c.input_tokens)} in (${n(c.cache_read_tokens)} cached, ${n(c.cache_write_tokens)} written) · ${n(c.output_tokens)} out`;
}

interface WordPage {
  id: string;
  word: string;
  bucket: string;
  type: 'game' | 'social';
  parent: { id: string; word: string } | null;
  bank_approved: boolean;
  chosen_sense: string | null;
  vet_definition: string | null;
  sense: { text: string; source: 'chosen' | 'vetted' | 'none' };
  // Word Bank scorer difficulty, 1-5 with its one-line reason; score null when not scored (2026-10-22).
  difficulty?: { score: number | null; reason: string | null; scored_at: string | null; vet_verdict: string | null };
  // Pronunciation check (2026-10-25): no AI; CMU / Wiktionary suggestion, Odin approves after Merriam-Webster.
  pronunciation?: Pronunciation;
  // Word audio (2026-10-30): made from the approved pronunciation, approved by Odin after listening.
  audio?: WordAudio;
  // Odin's saved directions (2026-10-27): each goes to its own call only.
  directions?: Record<DirectionKind, string>;
  // Odin's default directions (2026-10-31): a box with no note of its own is sent its default ('' = none).
  direction_defaults?: Partial<Record<DirectionKind, string>>;
  // The word's one joke pattern (2026-10-27), shared by Stage 1 and Stage 1b; null until Stage 1 picks one.
  joke_pattern?: JokePatternId | null;
  // Why the recommender chose it (2026-10-27); null when Odin picked it by hand.
  joke_pattern_reason?: string | null;
  // Each fun fact's source link (2026-10-31); null = no link. The result page links the fact's trailing source name to it.
  fact_urls?: Record<string, string | null>;
  content_approved_at: string | null;
  status: Status;
  fields: FieldRow[];
  stages: StagePanel[];
  all_stages_approved: boolean;
  // Ledger calls for this word (session content-<id>), with the cache split.
  cost?: WordCost | null;
}
interface SenseInfo {
  chosen_sense: string | null;
  senses: { part_of_speech: string | null; text: string }[];
  lookup_error: string | null;
}

const STATUS_LABEL: Record<Status, string> = {
  none: 'No content',
  generated: 'Ready to review',
  needs_fixes: 'Needs fixes',
  approved: 'Approved',
  missing: 'Missing',
};

const STAGE_MARK: Record<StageStatus, string> = {
  empty: 'empty',
  generated: 'generated',
  needs_fixes: 'needs fixes',
  approved: 'approved',
};

const FIELD_LABEL: Record<string, string> = {
  definition_enhanced: 'Enhanced definition',
  usage_context: 'Usage context',
  wrong_def_1: 'Fake 1', wrong_def_2: 'Fake 2', wrong_def_3: 'Fake 3',
  reveal_line_1: 'Reveal line 1', reveal_line_2: 'Reveal line 2', reveal_line_3: 'Reveal line 3',
  fake_tag_1: 'Tag 1', fake_tag_2: 'Tag 2', fake_tag_3: 'Tag 3',
  assignment: 'Assignment',
  slide_1_hook: 'Hook',
  etymology: 'Etymology',
  example_sentence_1: 'Example 1', example_sentence_2: 'Example 2', example_sentence_3: 'Example 3',
  how_to_use_1: 'How to use 1', how_to_use_2: 'How to use 2', how_to_use_3: 'How to use 3',
  fun_fact_1: 'Fun fact 1 (verified, sourced)',
  fun_fact_2: 'Fun fact 2 (verified, sourced)',
  fun_fact_3: 'Fun fact 3 (verified, sourced)',
  pronunciation_tip: 'Pronunciation tip',
  winner_reframe: 'Reframe: winner', loser_reframe: 'Reframe: out of guesses',
  second_chance_reframe_1: 'Reframe: second chance', third_chance_reframe_1: 'Reframe: third chance',
  sources_text: 'Sources',
  paa_q1: 'Q1', paa_a1: 'A1', paa_q2: 'Q2', paa_a2: 'A2', paa_q3: 'Q3', paa_a3: 'A3',
};

/**
 * The cost ledger names the old engine prompt each call ran, not the Content
 * stage. One Content stage can run several prompts: Stage 2 runs stage2
 * (examples), stage3 (how to use) and stage4 plus stage4-research (fun fact).
 * These group the ledger rows under the Content stage that made them.
 */
const PROMPT_STAGE: Record<string, string> = {
  stage1: '1', 'stage1-hook': '1', jokepattern: '1', stage1b: '1b', fakejudge: '1b', fakeshape: '1b', reveal: 'rv', assign: 'as', hook: 'hk',
  stage2: '2', stage3: '2', stage4: '2', 'stage4-research': '2',
  stage6: '3',
};
const PROMPT_NOTE: Record<string, string> = {
  stage2: '(examples)', stage3: '(how to use)', stage4: '(fun fact)', 'stage4-research': '(fun fact research)',
  stage6: '(reframes)',
};
const STAGE_ORDER = ['1', '1b', 'rv', 'as', 'hk', '2', '3'];

/**
 * The names Odin sees (2026-10-30, Odin's ruling), numbered 1 to 6 in tab
 * order. Display only: the stage ids, routes and stored data keep the old
 * pipeline names. The Hook is retired and has no number; its name stays for
 * old cost rows.
 */
const STAGE_NAMES: Record<string, string> = { 1: 'Meaning', '1b': 'Fakes', rv: 'Reveals', as: 'Assignment', hk: 'Hook', 2: 'Examples', 3: 'Win/Lose' };
const STAGE_NUMBERS: Record<string, number> = { 1: 1, '1b': 2, rv: 3, as: 4, 2: 5, 3: 6 };
/** "Fakes": for buttons and messages ("Approve Fakes"). */
function stageName(id: string): string {
  return STAGE_NAMES[id] || `Stage ${id}`;
}
/** "2 Fakes": for the tab and the stage heading. */
function stageTitle(id: string): string {
  return STAGE_NUMBERS[id] ? `${STAGE_NUMBERS[id]} ${stageName(id)}` : stageName(id);
}

function costGroups(rows: WordStageCost[]): { label: string; rows: WordStageCost[] }[] {
  // Stage 4 is retired (2026-10-22): calls made for another word in this
  // word's session (its old Related Words) and old stage8 calls group under Other.
  const groups = new Map<string, WordStageCost[]>();
  for (const r of rows) {
    const key = r.other_word ? 'other' : PROMPT_STAGE[r.stage_id] || 'other';
    groups.set(key, [...(groups.get(key) || []), r]);
  }
  return [...STAGE_ORDER, 'other'].filter((k) => groups.has(k))
    .map((k) => ({ label: k === 'other' ? 'Other' : stageName(k), rows: groups.get(k)! }));
}

function errorText(e: unknown): string {
  const err = e as { data?: { error?: string }; message?: string };
  return err?.data?.error || err?.message || 'Something went wrong. Nothing was changed.';
}

function StatusChip({ status }: { status: Status }) {
  return <span className={`ct-status ct-s-${status}`}>{STATUS_LABEL[status]}</span>;
}

type HistoryVersion = { id: number; stage: string; field: string; old_value: string; replaced_at: string; replaced_how: string; note?: string | null };

// Regenerate with direction (2026-10-02): one optional line for ONE Regenerate
// or Add another fake call. The worker appends it to that call only and keeps
// it in the field's history (or on the new fake). Hidden until the button is
// clicked: the click opens this inline row, which sends or cancels.
const DIRECTION_MAX = 200;
export function DirectionRow({
  value, onChange, onSend, onCancel, sendLabel, busy,
}: {
  value: string; onChange: (v: string) => void; onSend: () => void; onCancel: () => void;
  sendLabel: string; busy: boolean;
}) {
  return (
    <div className="ct-direction-row">
      <input
        className="ct-direction" type="text" maxLength={DIRECTION_MAX} value={value} disabled={busy} autoFocus
        placeholder="Direction for this regenerate (optional)"
        title={`Optional. One line, up to ${DIRECTION_MAX} characters, sent with this call only. Enter sends.`}
        onChange={(e) => onChange(e.target.value.replace(/[\r\n]+/g, ' '))}
        onKeyDown={(e) => { if (e.key === 'Enter' && !busy) { e.preventDefault(); onSend(); } }}
      />
      <button onClick={onSend} disabled={busy}>{sendLabel}</button>
      <button onClick={onCancel} disabled={busy}>Cancel</button>
    </div>
  );
}
const HOW_LABEL: Record<string, string> = {
  generate: 'replaced by Generate', regenerate: 'replaced by Regenerate', edit: 'replaced by Edit', restore: 'replaced by Restore',
  remove: 'removed',
};

/**
 * Field history (2026-09-28): every value this field has lost, newest first,
 * each with a Restore button. Restore writes the version back through the
 * Edit path, and the value it replaces joins the history.
 */
function FieldHistory({
  wordId, field, locked, onRestored,
}: { wordId: string; field: string; locked: boolean; onRestored: () => void }) {
  const [versions, setVersions] = useState<HistoryVersion[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const base = `/api/content/${encodeURIComponent(wordId)}`;

  const load = useCallback(async () => {
    try {
      const r = (await api.get(`${base}/field-history?field=${encodeURIComponent(field)}`)) as { versions: HistoryVersion[] };
      setVersions(r.versions || []);
    } catch (e) { setError(errorText(e)); }
  }, [base, field]);
  useEffect(() => { load(); }, [load]);

  const restore = async (v: HistoryVersion) => {
    setBusy(v.id); setError(null);
    try {
      await api.post(`${base}/restore-field`, { history_id: v.id });
      await load();
      onRestored();
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };

  return (
    <div className="ct-history">
      {versions === null && !error && <div className="ct-empty">Loading history...</div>}
      {versions && versions.length === 0 && <div className="ct-empty">No earlier versions.</div>}
      {versions && versions.map((v) => (
        <div key={v.id} className="ct-history-row">
          <div className="ct-history-meta">
            <span>{new Date(v.replaced_at).toLocaleString()}</span>
            <span className="ct-hint"> {HOW_LABEL[v.replaced_how] || v.replaced_how}</span>
            {!locked && (
              <button onClick={() => restore(v)} disabled={busy !== null}>
                {busy === v.id ? 'Restoring...' : 'Restore'}
              </button>
            )}
          </div>
          <div className="ct-value">{v.old_value}</div>
          {v.note && <div className="ct-check-note">Direction: {v.note}</div>}
        </div>
      ))}
      {error && <div className="ct-check-fail">{error}</div>}
    </div>
  );
}

/**
 * Move within a group (2026-10-31, Odin's ruling): a field goes up or down
 * within its own group only, never across. The worker swaps it with its
 * neighbour, history and checks with it: a fun fact takes its source link and
 * evidence, a question its answer, a picked fake its tag and reveal line. It
 * generates nothing and reopens the stage, as an Edit does. These are the
 * fields that carry the arrows; the number is the slot.
 */
const MOVE_LEAD = /^(example_sentence_|how_to_use_|fun_fact_|paa_q)([123])$/;
const MOVE_GROUP: Record<string, string> = {
  example_sentence_: 'Examples', how_to_use_: 'How to use',
  fun_fact_: 'Fun facts, with its source link', paa_q: 'People Also Ask, with its answer',
};

function MoveButtons({ wordId, field, up, down, disabled, group, onMoved, onError }: {
  wordId: string; field: string; up: boolean; down: boolean; disabled: boolean; group: string;
  onMoved: () => void; onError: (e: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const move = async (to: 'up' | 'down') => {
    setBusy(true); onError(null);
    try {
      const r = await api.post(`/api/content/${encodeURIComponent(wordId)}/move`, { field, to }) as { evidence_error?: string };
      // The move is saved; an evidence record that could not follow still shows.
      if (r?.evidence_error) onError(r.evidence_error);
      onMoved();
    } catch (e) { onError(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => move('up')} disabled={!up || disabled || busy} title={`Move up within ${group}`} aria-label="Move up">↑</button>
      <button onClick={() => move('down')} disabled={!down || disabled || busy} title={`Move down within ${group}`} aria-label="Move down">↓</button>
    </>
  );
}

export function FieldEditor({
  wordId, word, row, locked, onSaved, history = false, movable = false, moveBusy = false,
}: {
  wordId: string; word: string; row: FieldRow; locked: boolean; onSaved: () => void; history?: boolean;
  // Show the move arrows when this is a group's lead field (MOVE_LEAD); off while a run on the stage goes.
  movable?: boolean; moveBusy?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [draft, setDraft] = useState(row.value || '');
  const [busy, setBusy] = useState<'save' | 'regen' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState('');
  const [directing, setDirecting] = useState(false);
  // A Regenerate of this field still running, even one started before this page was reopened.
  const running = useRunStore((s) => s.runs.find((r) => r.kind === 'regenerate' && r.wordId === wordId && r.field === row.field));
  const saveDirections = useContext(DirectionDrafts).saveAll;

  useEffect(() => { setDraft(row.value || ''); }, [row.value]);

  const save = async () => {
    setBusy('save'); setError(null);
    try {
      const r = await api.put(`/api/content/${encodeURIComponent(wordId)}/field`, { field: row.field, value: draft }) as { learning_error?: string };
      setEditing(false);
      // The text is saved; a Learning store failure still shows (2026-10-22).
      if (r?.learning_error) setError(r.learning_error);
      onSaved();
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };
  const regen = async () => {
    setBusy('regen'); setError(null);
    try {
      if (!(await saveDirections())) return;
      const note = direction.trim();
      const r = await trackRun(
        { kind: 'regenerate', wordId, word, field: row.field, label: `Regenerate ${FIELD_LABEL[row.field] || row.field}` },
        () => api.post(`/api/content/${encodeURIComponent(wordId)}/regenerate-field`, note ? { field: row.field, note } : { field: row.field }) as Promise<{ learning_error?: string }>,
      );
      setDirection('');
      setDirecting(false);
      if (r?.learning_error) setError(r.learning_error);
      onSaved();
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };

  const failing = row.check?.blocking;
  const slot = movable ? row.field.match(MOVE_LEAD) : null;
  return (
    <div className={`ct-field ${failing ? 'ct-field-fail' : ''}`}>
      <div className="ct-field-head">
        <span className="ct-field-name">{FIELD_LABEL[row.field] || row.field}{row.optional && !row.advisory ? ' (may be blank)' : ''}</span>
        {failing && <span className="ct-needs-fixes">needs fixes</span>}
        {(!locked || history) && (
          <span className="ct-field-actions">
            {!locked && slot && (
              <MoveButtons wordId={wordId} field={row.field} up={slot[2] !== '1'} down={slot[2] !== '3'}
                disabled={!!busy || !!running || editing || moveBusy} group={MOVE_GROUP[slot[1]]} onMoved={onSaved} onError={setError} />
            )}
            {!locked && !editing && <button onClick={() => setEditing(true)} disabled={!!busy}>Edit</button>}
            {!locked && (
              <button onClick={() => { setDirecting(true); setError(null); }} disabled={!!busy || !!running || directing || editing} title="Regenerate this field only"
                className={running ? 'run-pulse' : undefined}>
                {running ? <>Regenerating... <RunTimer startedAt={running.startedAt} /></> : 'Regenerate'}
              </button>
            )}
            {history && (
              <button onClick={() => setShowHistory((x) => !x)} title="Earlier versions of this field">
                {showHistory ? 'Hide history' : 'History'}
              </button>
            )}
          </span>
        )}
      </div>
      {!locked && directing && (
        <DirectionRow value={direction} onChange={setDirection} onSend={regen}
          onCancel={() => { setDirecting(false); setDirection(''); }}
          sendLabel={busy === 'regen' ? 'Regenerating...' : 'Regenerate'} busy={!!busy || !!running} />
      )}
      {history && showHistory && <FieldHistory key={row.value || ''} wordId={wordId} field={row.field} locked={locked} onRestored={onSaved} />}
      {editing ? (
        <div className="ct-edit">
          <textarea value={draft} rows={3} onChange={(e) => setDraft(e.target.value)} />
          <div className="ct-edit-actions">
            <button onClick={save} disabled={!!busy}>{busy === 'save' ? 'Saving...' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setDraft(row.value || ''); setError(null); }} disabled={!!busy}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="ct-value">{row.value || <span className="ct-empty">(empty)</span>}</div>
      )}
      {row.check && (
        <div className={row.check.blocking ? 'ct-check-fail' : 'ct-check-note'}>
          {row.check.blocking ? 'Doctrine check failed: ' : row.advisory ? 'Check (does not block approval): ' : 'Note: '}{row.check.reason}
        </div>
      )}
      {error && <div className="ct-check-fail">{error}</div>}
    </div>
  );
}

/**
 * A fun fact's source link (2026-10-31, Odin's ruling): the result page turns
 * the fact's trailing " - <Source name>" into a link to it. Filled from the
 * evidence record when the fact is generated; Odin can type or change it here
 * (https only, empty for no link). Saving it changes no text and no stage, so
 * it stays open on approved content (Odin 2026-10-31).
 */
function FactUrlBox({ wordId, field, url, locked, onSaved }: {
  wordId: string; field: string; url: string | null; locked: boolean; onSaved: () => void;
}) {
  const [draft, setDraft] = useState(url || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setDraft(url || ''); }, [url]);
  const changed = draft.trim() !== (url || '');
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.put(`/api/content/${encodeURIComponent(wordId)}/fact-url`, { field, url: draft.trim() });
      onSaved();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ct-fact-url">
      <span className="ct-fact-url-label">Source link</span>
      {locked
        ? (url ? <a href={url} target="_blank" rel="noopener noreferrer">{url}</a> : <span className="ct-empty">(none)</span>)
        : (
          <>
            <input type="url" value={draft} placeholder="https://... (empty = no link)" onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && changed && !busy) save(); }} disabled={busy} />
            {changed && (
              <>
                <button onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save'}</button>
                <button onClick={() => { setDraft(url || ''); setError(null); }} disabled={busy}>Cancel</button>
              </>
            )}
            {!changed && url && <a href={url} target="_blank" rel="noopener noreferrer">Open</a>}
          </>
        )}
      {error && <div className="ct-check-fail">{error}</div>}
    </div>
  );
}

/**
 * Stage 1b: every kept fake in one numbered list (FAKE 1 to FAKE N, in
 * candidate order), each with its pick box, Edit and text, shown newest first
 * with its number kept (2026-10-30). Add another fake adds FAKE N+1 at the top. A picked fake is game fake wrong_def_1..3
 * by its place among the picked ones. Edit works on every fake (2026-10-01):
 * stage/1b/edit saves a picked one through its wrong_def field and an unpicked
 * one in the candidate list, and keeps the old text in field_history either way.
 * Regenerate works on every fake too (2026-10-22): one Stage 1b call with an
 * optional direction replaces only that fake; the others and the pick stay.
 */
export function FakeCandidates({
  wordId, word, candidates, fields, locked, onChanged, judge, picksBy,
}: {
  wordId: string; word: string; candidates: FakeCandidate[]; fields: FieldRow[]; locked: boolean; onChanged: () => void;
  judge?: StagePanel['judge']; picksBy?: StagePanel['picks_by'];
}) {
  // Quick saves only (pick, Edit's Save, Remove). Model runs (Regenerate, Add
  // another fake, Judge again) are read from the run store and lock only their
  // own fake or button (2026-10-22): the rest of the list stays usable while
  // they run, and the worker merges overlapping changes.
  const [busy, setBusy] = useState<'pick' | 'save' | 'remove' | null>(null);
  const judging = useRunStore((s) => s.runs.find((r) => r.kind === 'regenerate' && r.wordId === wordId && r.field === 'fake-judge'));
  const fakeLabel = (id: string) => `FAKE ${candidates.findIndex((c) => c.id === id) + 1}`;
  const adding = useRunStore((s) => s.runs.find((r) => r.kind === 'fake' && r.wordId === wordId));
  // Regenerates of single fakes still running, keyed by run field "fake:<id>".
  // Select the store's own array and filter here: a selector that returns a
  // new array on every call loops the render forever in zustand 5 (the blank
  // page of 2026-10-22, deployment b748faaa).
  const allRuns = useRunStore((s) => s.runs);
  const regenRuns = allRuns.filter((r) => r.kind === 'regenerate' && r.wordId === wordId && r.field?.startsWith('fake:'));
  const regenerating = (id: string) => regenRuns.some((r) => r.field === `fake:${id}`);
  const [regenId, setRegenId] = useState<string | null>(null);
  const [regenDirection, setRegenDirection] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [direction, setDirection] = useState('');
  const [directing, setDirecting] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const picked = candidates.filter((c) => c.selected);
  const saveDirections = useContext(DirectionDrafts).saveAll;
  const url = (tail: string) => `/api/content/${encodeURIComponent(wordId)}/stage/1b/${tail}`;

  const toggle = async (c: FakeCandidate) => {
    const ids = c.selected ? picked.filter((x) => x.id !== c.id).map((x) => x.id) : [...picked.map((x) => x.id), c.id];
    setBusy('pick'); setError(null); setNote(null);
    try { await api.post(url('select'), { ids }); onChanged(); } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };
  const add = async () => {
    setError(null); setNote(null);
    // A failed save keeps the row open with its note; the box shows why.
    if (!(await saveDirections())) return;
    const d = direction.trim();
    setDirection('');
    setDirecting(false);
    try {
      const r = await trackRun(
        { kind: 'fake', wordId, word, stage: '1b', label: 'Add another fake' },
        () => api.post(url('add-fake'), d ? { note: d } : {}) as Promise<{ refused_candidates?: Record<string, string> } & JudgeResult>,
      );
      const refused = Object.keys(r.refused_candidates || {});
      setNote([refused.length
        ? `Added FAKE ${candidates.length + 1}. It needs fixes: edit it until it passes the Doctrine check before it can be picked.`
        : `Added FAKE ${candidates.length + 1}. Pick it to use it.`, judgeNote(r, fakeLabel)].filter(Boolean).join(' '));
      onChanged();
    } catch (e) { setError(errorText(e)); }
  };
  // Remove (2026-09-28): the removed text goes to field_history, restorable
  // from "Removed fakes". Removing a picked fake unpicks it. Remove all
  // unpicked leaves out a fake that is regenerating (2026-10-22).
  const remove = async (ids: string[] | null) => {
    setBusy('remove'); setError(null); setNote(null);
    const running = candidates.filter((c) => regenerating(c.id)).map((c) => c.id);
    const body = ids ? { ids }
      : running.length ? { ids: candidates.filter((c) => !c.selected && !running.includes(c.id)).map((c) => c.id) }
        : { all_unpicked: true };
    try {
      const r = await api.post(url('remove'), body) as { removed?: string[]; reopened?: string | null };
      const n = (r.removed || []).length;
      setNote(n
        ? `Removed ${n} fake${n === 1 ? '' : 's'}. ${n === 1 ? 'It is' : 'They are'} kept under Removed fakes.${r.reopened ? ' Fakes is reopened.' : ''}`
        : 'Nothing to remove.');
      onChanged();
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };
  const regenerate = async (c: FakeCandidate, n: number) => {
    setError(null); setNote(null);
    if (!(await saveDirections())) return;
    // The direction row closes at once; the fake's button carries the run.
    const d = regenDirection.trim();
    setRegenId(null);
    setRegenDirection('');
    try {
      const r = await trackRun(
        { kind: 'regenerate', wordId, word, field: `fake:${c.id}`, label: `Regenerate FAKE ${n}` },
        () => api.post(url('regenerate'), d ? { id: c.id, note: d } : { id: c.id }) as Promise<{ refused_candidates?: Record<string, string>; reopened?: string | null; learning_error?: string } & JudgeResult>,
      );
      const parts = [`Regenerated FAKE ${n}.`];
      if (r.refused_candidates?.[c.id]) parts.push('It needs fixes: edit it until it passes the Doctrine check.');
      if (r.reopened) parts.push('Fakes is reopened.');
      if (c.selected) parts.push('Its reveal line and tag were written for the old fake: regenerate them in Reveals.');
      if (r.learning_error) parts.push(r.learning_error);
      const j = judgeNote(r, fakeLabel);
      if (j) parts.push(j);
      setNote(parts.join(' '));
      onChanged();
    } catch (e) { setError(errorText(e)); }
  };
  // Judge again (2026-10-22): the judge rates every kept fake now. It pre-ticks
  // only while Odin has not picked; his picks are never changed.
  const judgeAgain = async () => {
    setError(null); setNote(null);
    try {
      const r = await trackRun(
        { kind: 'regenerate', wordId, word, field: 'fake-judge', label: 'Judge the fakes' },
        () => api.post(url('judge'), {}) as Promise<JudgeResult>,
      );
      setNote(judgeNote(r, fakeLabel));
      onChanged();
    } catch (e) { setError(errorText(e)); }
  };
  const unpicked = candidates.filter((c) => !c.selected && !regenerating(c.id)).length;
  // A refused save shows its reason under the open editor, not at the foot of
  // the list, and a good save says so (2026-10-01).
  const [editError, setEditError] = useState<string | null>(null);
  const save = async (c: FakeCandidate, n: number) => {
    setBusy('save'); setError(null); setNote(null); setEditError(null);
    try {
      const r = await api.post(url('edit'), { id: c.id, text: draft }) as { saved?: string[]; reopened?: string | null; learning_error?: string };
      setEditId(null);
      setNote((r.saved || []).length || draft.trim() !== c.text
        ? `Saved FAKE ${n}.${r.reopened ? ' Fakes is reopened.' : ''}${r.learning_error ? ` ${r.learning_error}` : ''}`
        : `FAKE ${n} is unchanged.`);
      onChanged();
    } catch (e) { setEditError(errorText(e)); } finally { setBusy(null); }
  };

  return (
    <div className="ct-fields">
      <div className="ct-field-head">
        <span className="ct-field-name">Fakes: {picked.length} of 3 picked ({candidates.length} kept)</span>
        <span className="ct-field-actions">
          {!locked && (
            <button onClick={() => { setDirecting(true); setError(null); setNote(null); }} disabled={!!adding || directing}
              className={adding ? 'run-pulse' : undefined}>
              {adding ? <>Adding... <RunTimer startedAt={adding.startedAt} /></> : 'Add another fake'}
            </button>
          )}
          {!locked && (
            <button onClick={() => remove(null)} disabled={!!busy || unpicked === 0} title="Remove every fake that is not picked">
              {busy === 'remove' ? 'Removing...' : `Remove all unpicked (${unpicked})`}
            </button>
          )}
          {!locked && (
            <button onClick={judgeAgain} disabled={!!judging || candidates.length === 0}
              className={judging ? 'run-pulse' : undefined}
              title="A second model rates every fake and names its best three. It picks them only if you have not picked.">
              {judging ? <>Judging... <RunTimer startedAt={judging.startedAt} /></> : 'Judge again'}
            </button>
          )}
          <button onClick={() => setShowRemoved((x) => !x)} title="Fakes removed from this list and earlier text of edited unpicked fakes, with Restore">
            {showRemoved ? 'Hide removed fakes' : 'Removed fakes'}
          </button>
        </span>
      </div>
      {!locked && directing && (
        <DirectionRow value={direction} onChange={setDirection} onSend={add}
          onCancel={() => { setDirecting(false); setDirection(''); }}
          sendLabel="Add another fake" busy={!!adding} />
      )}
      {judge && (
        <div className="ct-check-note">
          Judge{judge.model ? ` (${judge.model})` : ''}: top three {judge.top3.map(fakeLabel).join(', ')}.
          {' '}{picksBy === 'judge' ? 'These are pre-ticked. Change any pick before you approve.' : picksBy === 'odin' ? 'The picks are yours.' : ''}
        </div>
      )}
      {judge?.odd_one_out?.flag && (
        <div className="ct-check-note">Real one stands out{judge.odd_one_out.reason ? `: ${judge.odd_one_out.reason}` : '.'}</div>
      )}
      {showRemoved && (
        <FieldHistory key={candidates.map((c) => c.id).join(',')} wordId={wordId} field="fake_candidate" locked={locked} onRestored={onChanged} />
      )}
      {newestFirst(candidates).map(({ c, n }) => {
        const slot = picked.findIndex((x) => x.id === c.id);
        const check = slot >= 0 ? fields.find((f) => f.field === `wrong_def_${slot + 1}`)?.check || null : null;
        const editing = editId === c.id;
        const regenRun = regenRuns.find((r) => r.field === `fake:${c.id}`);
        return (
          <div key={c.id} className={`ct-field ${c.refused || check?.blocking ? 'ct-field-fail' : ''}`}
            style={c.selected ? { background: 'rgba(107,142,94,0.12)' } : undefined}>
            <div className="ct-field-head">
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox" checked={c.selected}
                  disabled={locked || !!busy || !!regenRun || !!c.refused || (!c.selected && picked.length >= 3)}
                  onChange={() => toggle(c)}
                />
                <span className="ct-field-name">FAKE {n}</span>
                {c.selected && <span className="ct-pos">picked</span>}
                {c.judge && (
                  <span className={`ct-judge-score${judge?.top3.includes(c.id) ? ' ct-judge-top' : ''}`} title="The fake judge's score, 1 to 10">
                    {c.judge.score}/10{judge?.top3.includes(c.id) ? ' judge pick' : ''}
                  </span>
                )}
                {(c.regenerated_at || c.created_at) && (
                  <span className="ct-hint" style={{ fontSize: '0.85em' }}>made {centralTime((c.regenerated_at || c.created_at) as string)}</span>
                )}
                {(c.refused || check?.blocking) && <span className="ct-needs-fixes">needs fixes</span>}
              </label>
              {!editing && (!locked || c.selected) && (
                <span className="ct-field-actions">
                  {!locked && (
                    <button disabled={!!busy || !!regenRun} title={regenRun ? 'This fake is regenerating' : 'Edit this fake'}
                      onClick={() => { setEditId(c.id); setDraft(c.text); setError(null); setEditError(null); }}>Edit</button>
                  )}
                  {!locked && (
                    <button disabled={!!regenRun || regenId === c.id} title="Regenerate this fake only"
                      className={regenRun ? 'run-pulse' : undefined}
                      onClick={() => { setRegenId(c.id); setRegenDirection(''); setError(null); setNote(null); }}>
                      {regenRun ? <>Regenerating... <RunTimer startedAt={regenRun.startedAt} /></> : 'Regenerate'}
                    </button>
                  )}
                  {!locked && (
                    <button disabled={!!busy || !!regenRun} onClick={() => remove([c.id])}
                      title={c.selected ? 'Remove this fake. It is unpicked and kept under Removed fakes.' : 'Remove this fake. It is kept under Removed fakes.'}>
                      Remove
                    </button>
                  )}
                  {c.selected && (
                    <button onClick={() => setHistoryId(historyId === c.id ? null : c.id)}
                      title={`Earlier versions of game fake ${slot + 1}`}>
                      {historyId === c.id ? 'Hide history' : 'History'}
                    </button>
                  )}
                </span>
              )}
            </div>
            {!locked && regenId === c.id && !editing && (
              <DirectionRow value={regenDirection} onChange={setRegenDirection} onSend={() => regenerate(c, n)}
                onCancel={() => { setRegenId(null); setRegenDirection(''); }}
                sendLabel="Regenerate" busy={!!regenRun} />
            )}
            {c.selected && historyId === c.id && (
              <FieldHistory key={c.text} wordId={wordId} field={`wrong_def_${slot + 1}`} locked={locked} onRestored={onChanged} />
            )}
            {editing ? (
              <div className="ct-edit">
                <textarea value={draft} rows={3} onChange={(e) => setDraft(e.target.value)} />
                <div className="ct-edit-actions">
                  <button onClick={() => save(c, n)} disabled={!!busy}>{busy === 'save' ? 'Saving...' : 'Save'}</button>
                  <button onClick={() => { setEditId(null); setEditError(null); }} disabled={!!busy}>Cancel</button>
                </div>
                {editError && <div className="ct-check-fail">{editError}</div>}
              </div>
            ) : (
              <div className="ct-value">{c.text}</div>
            )}
            {c.judge?.reason && <div className="ct-judge-reason">Judge: {c.judge.reason}</div>}
            {c.note && <div className="ct-check-note">Direction: {c.note}</div>}
            {c.refused && <div className="ct-check-fail">Doctrine check failed: {c.refused}</div>}
            {!c.refused && check && (
              <div className={check.blocking ? 'ct-check-fail' : 'ct-check-note'}>
                {check.blocking ? 'Doctrine check failed: ' : 'Note: '}{check.reason}
              </div>
            )}
          </div>
        );
      })}
      {picked.length !== 3 && <span className="ct-hint">Pick exactly three to approve Fakes.</span>}
      {note && <div className="ct-result">{note}</div>}
      {error && <div className="ct-check-fail">{error}</div>}
    </div>
  );
}

/**
 * Reveal lines (2026-10-03): each picked fake, struck through as the player
 * sees it, with its tag (2026-10-04) and its reveal line under it. Edit,
 * Regenerate (with direction) and History work exactly as on every other field.
 */
function RevealLines({
  wordId, word, stage, locked, busy, onSaved,
}: { wordId: string; word: string; stage: StagePanel; locked: boolean; busy: boolean; onSaved: () => void }) {
  const fakes = stage.picked_fakes || [];
  const byName = new Map(stage.fields.map((f) => [f.field, f]));
  const [moveError, setMoveError] = useState<string | null>(null);
  return (
    <div className="ct-fields">
      {moveError && <div className="ct-check-fail">{moveError}</div>}
      {[1, 2, 3].map((n, i) => {
        const tag = byName.get(`fake_tag_${n}`);
        const line = byName.get(`reveal_line_${n}`);
        return (
          <div key={n}>
            <div className="ct-field">
              <div className="ct-field-head">
                <span className="ct-field-name">Fake {n} (picked)</span>
                {/* A picked fake moves with its tag and reveal line (2026-10-31). */}
                {!locked && fakes[i] && (
                  <span className="ct-field-actions">
                    <MoveButtons wordId={wordId} field={`wrong_def_${n}`} up={i > 0} down={!!fakes[i + 1]} disabled={busy}
                      group="the picked fakes, with its tag and reveal line" onMoved={onSaved} onError={setMoveError} />
                  </span>
                )}
              </div>
              <div className="ct-value" style={{ textDecoration: 'line-through', opacity: 0.7 }}>
                {fakes[i] || <span className="ct-empty">(no fake picked)</span>}
              </div>
            </div>
            {tag && <FieldEditor wordId={wordId} word={word} row={tag} locked={locked} onSaved={onSaved} history />}
            {line && <FieldEditor wordId={wordId} word={word} row={line} locked={locked} onSaved={onSaved} history />}
          </div>
        );
      })}
      {fakes.length !== 3 && <span className="ct-hint">Pick three fakes in Fakes to generate reveal lines.</span>}
    </div>
  );
}

/** Stage 1b: the approved Stage 1 enhanced definition and the vetted definition, read-only, to compare against. */
// Your direction (2026-10-27): Odin's note for this word, saved with it and
// sent with every Generate and Regenerate of its call until changed. Each
// note goes to its own call only (the worker enforces it): the Stage 1 note to
// Stage 1, the Stage 1b note to Stage 1b, and (directions 2) one each for
// Reveal lines, Assignment, Hook, the three Stage 2 calls and the Stage 3
// reframes. Editable any time, approved content included.
// Default directions (2026-10-31): a box with no note of its own shows Odin's
// default from Settings, marked Default, and the worker sends that default.
// Saving a different text sets a note for this word, which wins; Use default
// clears it.
const SAVED_DIRECTION_MAX = 500;
type DirectionKind = 'definition' | 'fakes' | 'reveal' | 'assign' | 'hook' | 'examples' | 'howtouse' | 'funfacts' | 'reframes';
const DIRECTION_HINT: Record<DirectionKind, string> = {
  definition: 'Angle, image, tone. Never changes accuracy.',
  fakes: 'Themes, feel, topics. Steers the fakes; never changes their shape.',
  reveal: 'Angle, subject, tone. Never leaks the real meaning.',
  assign: 'The dare, the target, the tone. The assignment rules still hold.',
  hook: 'Angle and tone. Never hints at the meaning.',
  examples: 'Situations, settings, tone.',
  howtouse: 'Settings, audiences, tone.',
  funfacts: 'Which facts to look for. Never changes the source check.',
  reframes: 'Angle and tone. With a note or a default, Generate writes fresh reframes.',
};
// What the note steers, for its label and placeholder.
const DIRECTION_FOR: Record<DirectionKind, string> = {
  definition: 'the enhanced definition', fakes: 'the fakes', reveal: 'the reveal lines and tags',
  assign: 'the assignment', hook: 'the hook', examples: 'the example sentences', howtouse: 'the how to use tips',
  funfacts: 'the fun facts', reframes: 'the reframes',
};
// An example in each box, so the kind of note it takes is plain at a glance.
const DIRECTION_EXAMPLE: Record<DirectionKind, string> = {
  definition: 'e.g. Lean on the everyday office version of this. Keep the image plain.',
  fakes: 'e.g. Kitchen tools and sailing. One absurd, the rest dead serious.',
  reveal: 'e.g. Tease the player for trusting anything that sounds official.',
  assign: 'e.g. Aim it at group chats. The dare happens in writing.',
  hook: 'e.g. Play on how the word sounds out loud.',
  examples: 'e.g. Office settings and family dinners. No sports.',
  howtouse: 'e.g. Work email and texting. One formal, one casual.',
  funfacts: 'e.g. History angle. Who first used it, and when.',
  reframes: 'e.g. The winner gets a backhanded compliment. The loser gets a dare for tomorrow.',
};
// Stage 2 makes three calls, so its three boxes are named.
const DIRECTION_NAME: Partial<Record<DirectionKind, string>> = { examples: 'Examples', howtouse: 'How to use', funfacts: 'Fun facts' };
const STAGE_DIRECTIONS: Record<string, DirectionKind[]> = {
  '1': ['definition'], '1b': ['fakes'], rv: ['reveal'], as: ['assign'], hk: ['hook'],
  '2': ['examples', 'howtouse', 'funfacts'], '3': ['reframes'],
};
// Generate and Regenerate in the open stage panel save its unsaved boxes first
// (Odin's ruling 2026-10-28). Each box with an unsaved change registers its
// save here; saveAll runs them and is false when one failed, so nothing runs.
const DirectionDrafts = createContext<{
  register: (kind: DirectionKind, save: DraftSave) => () => void;
  saveAll: () => Promise<boolean>;
}>({ register: () => () => {}, saveAll: async () => true });
function YourDirection({ page, kind, onSaved }: { page: WordPage; kind: DirectionKind; onSaved: () => void }) {
  const saved = page.directions?.[kind] ?? '';
  const fallback = kind === 'hook' ? '' : (page.direction_defaults?.[kind] ?? '');
  // What the box holds until edited: the word's own note, else the default.
  const shown = saved || fallback;
  const [draft, setDraft] = useState(shown);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const register = useContext(DirectionDrafts).register;
  useEffect(() => { setDraft(shown); setError(null); }, [shown, page.id]);
  const changed = draftChanged(draft, shown);
  // A box cleared to empty saves empty, as Save does.
  const put = useCallback(async (text: string): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      await api.put(`/api/content/${encodeURIComponent(page.id)}/direction`, { kind, text });
      onSaved();
      return true;
    } catch (e) { setError(errorText(e)); return false; } finally { setBusy(false); }
  }, [page.id, kind, onSaved]);
  const save = useCallback(() => put(draft), [put, draft]);
  useEffect(() => (changed ? register(kind, save) : undefined), [changed, register, kind, save]);
  if (!page.directions || !(kind in page.directions)) return null;
  const name = DIRECTION_NAME[kind];
  return (
    <div className={`ct-your-direction${saved ? ' is-set' : ''}`}>
      <div className="ct-your-direction-head">
        <span className="ct-your-direction-label">{name ? `Your direction: ${name}` : 'Your direction'}</span>
        {saved && !changed && <span className="ct-your-direction-saved">Set for this word</span>}
        {!saved && fallback && !changed && <span className="ct-your-direction-default">Default</span>}
        {saved && !changed && (
          <button className="ct-secondary" disabled={busy} onClick={() => void put('')}
            title={fallback ? 'Clear this word\'s note and send the default from Settings.' : 'Clear this word\'s note. No default is set for this box.'}>
            Use default
          </button>
        )}
      </div>
      <span className="ct-hint">{DIRECTION_HINT[kind]} Sent with every Generate and Regenerate of {DIRECTION_FOR[kind]}.</span>
      <textarea
        rows={3} maxLength={SAVED_DIRECTION_MAX} value={draft} disabled={busy}
        aria-label={`Your direction for ${DIRECTION_FOR[kind]}`}
        placeholder={DIRECTION_EXAMPLE[kind]}
        onChange={(e) => { setDraft(e.target.value.replace(/[\r\n]+/g, ' ')); setError(null); }}
      />
      {changed && (
        <div className="ct-pron-row">
          <button className="ct-secondary" disabled={busy} onClick={save}>{busy ? 'Saving...' : 'Save direction'}</button>
          <button className="ct-secondary" disabled={busy} onClick={() => { setDraft(saved); setError(null); }}>Cancel</button>
          <span className="ct-hint">{!saved && !draft.trim() ? 'An empty box saves nothing; the default still applies.' : 'Not saved yet. Generate will save it first.'}</span>
        </div>
      )}
      {kind === 'definition' && <JokePatternPicker page={page} onSaved={onSaved} />}
      {kind === 'fakes' && <JokePatternPicker page={page} readOnly />}
      {error && <div className="ct-check-fail">{error}</div>}
    </div>
  );
}

// Joke pattern (2026-10-27, funnier tone): the word's ONE pattern, used by the
// enhanced definition and every fake. Chosen in the Stage 1 panel (saved on
// change, applies to the next Generate or Regenerate), shown read-only in
// Stage 1b's. Recipes from the approved gold set (Definition Tone v5).
// Stage 1 recommends one when none is saved (2026-10-27) and its reason shows
// under the dropdown; Recommend runs the recommender again any time and
// replaces the saved pattern only once it returns. A hand pick clears the reason.
type JokePatternId = 'scene' | 'triple' | 'takeoff' | 'comparison' | 'reversal';
const JOKE_PATTERNS: { id: JokePatternId; name: string; recipe: string }[] = [
  { id: 'scene', name: 'Specific Scene', recipe: 'Plain meaning, then one exact, recognizable moment of it. Punch is the detail at the end.' },
  { id: 'takeoff', name: 'Take-off', recipe: 'Plain meaning, then a believable consequence pushed to an extreme, stated flat.' },
  { id: 'comparison', name: 'Unflattering Comparison', recipe: 'Plain meaning, then "like a..." or "as X as a...", a short deflating comparison.' },
  { id: 'reversal', name: 'Reversal', recipe: 'Plain meaning, then a clause that heads one way and lands somewhere else in the last words.' },
  { id: 'triple', name: 'Comic Triple', recipe: 'Plain meaning, then three items, the third breaks the pattern. One or two words each.' },
];
// Every fallback reason starts with this (workers/src/services/patternRecommend.js RANDOM_REASON_PREFIX).
const RANDOM_REASON_PREFIX = 'Picked at random';
function JokePatternPicker({ page, onSaved, readOnly = false }: { page: WordPage; onSaved?: () => void; readOnly?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [recommending, setRecommending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!('joke_pattern' in page)) return null;
  const current = JOKE_PATTERNS.find((p) => p.id === page.joke_pattern) || null;
  const reason = page.joke_pattern_reason || '';
  const recommend = async () => {
    setRecommending(true); setError(null);
    try {
      await trackRun(
        { kind: 'regenerate', wordId: page.id, word: page.word, stage: '1', field: 'joke_pattern', label: 'Recommend joke pattern' },
        () => api.post(`/api/content/${encodeURIComponent(page.id)}/joke-pattern/recommend`, {}),
      );
      onSaved?.();
    } catch (e) { setError(errorText(e)); } finally { setRecommending(false); }
  };
  const pick = async (id: string) => {
    if (!id || id === page.joke_pattern) return;
    setBusy(true); setError(null);
    try {
      await api.put(`/api/content/${encodeURIComponent(page.id)}/joke-pattern`, { pattern: id });
      onSaved?.();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ct-joke-pattern">
      <div className="ct-pron-row">
        <span className="ct-joke-pattern-label">Joke pattern</span>
        {readOnly
          ? <span>{current ? current.name : 'Not picked yet'}</span>
          : (
            <select value={current?.id || ''} disabled={busy || recommending} aria-label="Joke pattern" onChange={(e) => pick(e.target.value)}>
              {!current && <option value="">Not picked yet</option>}
              {JOKE_PATTERNS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
        {!readOnly && (
          <button className="ct-secondary" disabled={busy || recommending} onClick={recommend}>
            {recommending ? 'Recommending...' : 'Recommend'}
          </button>
        )}
        {busy && <span className="ct-hint">Saving...</span>}
      </div>
      {!readOnly && current && reason && (
        <span className="ct-joke-pattern-reason">
          {reason.startsWith(RANDOM_REASON_PREFIX) ? `${current.name}. ${reason}` : `Recommended: ${current.name}. ${reason}`}
        </span>
      )}
      <span className="ct-hint">
        {current ? current.recipe : 'Meaning recommends one the first time it generates the definition, or press Recommend.'}
        {' '}{readOnly ? 'Set in Meaning. The fakes use the same pattern as the real definition.' : 'Used by the definition and every fake from the next Generate or Regenerate.'}
      </span>
      {error && <div className="ct-check-fail">{error}</div>}
    </div>
  );
}

function FakeReference({ page }: { page: WordPage }) {
  const s1 = page.stages.find((s) => s.id === '1');
  const enhanced = s1?.fields.find((f) => f.field === 'definition_enhanced')?.value || null;
  return (
    <div className="ct-fields" style={{ marginBottom: 12 }}>
      <div className="ct-field">
        <div className="ct-field-head">
          <span className="ct-field-name">Enhanced definition (Meaning{s1?.status === 'approved' ? ', approved' : ', not approved'})</span>
        </div>
        <div className="ct-value">{enhanced || <span className="ct-empty">(empty)</span>}</div>
      </div>
      <div className="ct-field">
        <div className="ct-field-head"><span className="ct-field-name">Vetted definition</span></div>
        <div className="ct-value">{page.vet_definition || <span className="ct-empty">(none on record)</span>}</div>
      </div>
    </div>
  );
}

type Pronunciation =
  | { available: false }
  | {
    available: true; phonetics: string | null; suggested: string | null;
    source: 'both' | 'cmu' | 'wiktionary' | 'none' | null; status: 'auto' | 'approved' | 'needs_check' | null;
    mw_url: string; needs_check: boolean;
  };

const PRON_STATUS: Record<string, { label: string; cls: string }> = {
  auto: { label: 'auto: CMU and Wiktionary agree', cls: 'ct-s-generated' },
  approved: { label: 'approved: Merriam-Webster checked', cls: 'ct-s-approved' },
  needs_check: { label: 'needs a check', cls: 'ct-s-needs_fixes' },
};
const PRON_SOURCE: Record<string, string> = {
  both: 'CMU and Wiktionary (they agree)', cmu: 'CMU only', wiktionary: 'Wiktionary only', none: 'neither source has this word',
};

/**
 * Pronunciation (2026-10-25, Odin's ruling: 100% accurate, never AI). Shows
 * words.phonetics and its status. A word that needs a check opens the check:
 * the suggestion and its source, a Merriam-Webster link, a field prefilled with
 * the suggestion and Approve. Publish refuses the word until it is auto or approved.
 * Paste box (2026-10-25): MW's pasted text is converted by fixed rules
 * (utils/mwRespell.ts) into the field; Odin still approves. After Approve the
 * next word needing a check opens, Game words first, earliest date first.
 */
function PronunciationLine({ page, onChanged }: { page: WordPage; onChanged: () => void }) {
  const p = page.pronunciation;
  const ready = !!p && p.available;
  const [open, setOpen] = useState(ready && p.needs_check);
  const [draft, setDraft] = useState(ready ? (p.suggested || p.phonetics || '') : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const [pasteFail, setPasteFail] = useState(false);
  const openWord = useUiStore((s) => s.openContentWord);
  if (!p) return null;
  if (!p.available) {
    return <div className="ct-difficulty">Pronunciation: <span className="ct-empty">check not set up on the database yet</span></div>;
  }
  const status = p.status ? PRON_STATUS[p.status] : null;
  const approve = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/content/${encodeURIComponent(page.id)}/pronunciation`, { phonetics: draft });
      setOpen(false);
      await useBinderStore.getState().fetchBinder();
      const { game, social } = useBinderStore.getState();
      const next = nextPronunciationCheck(game, social, page.id);
      if (next) openWord(next.id, null);
      else onChanged();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const paste = (text: string) => {
    setPasted(text);
    if (!text.trim()) { setPasteFail(false); return; }
    const converted = mwToRespelling(text);
    setPasteFail(!converted);
    if (converted) { setDraft(converted); setError(null); }
  };
  return (
    <div className="ct-difficulty ct-pron">
      Pronunciation: {p.phonetics ? <b>{p.phonetics}</b> : <span className="ct-empty">none</span>}{' '}
      <span className={`ct-status ${status ? status.cls : 'ct-s-none'}`}>{status ? status.label : 'not checked'}</span>{' '}
      {!open && <button className="ct-link" onClick={() => { setDraft(p.suggested || p.phonetics || ''); setOpen(true); }}>Check</button>}
      {p.needs_check && <span className="ct-hint"> · Publish waits on this</span>}
      {open && (
        <div className="ct-pron-check">
          <div>
            Suggestion: {p.suggested ? <b>{p.suggested}</b> : <span className="ct-empty">none found</span>}
            <span className="ct-hint"> · from {PRON_SOURCE[p.source || 'none']}</span>
          </div>
          <div>
            <a href={p.mw_url} target="_blank" rel="noopener noreferrer">Check Merriam-Webster for "{page.word}"</a>
            <span className="ct-hint"> · Merriam-Webster wins over the free sources. Write it in our respelling, stressed syllable in capitals.</span>
          </div>
          <div className="ct-pron-row">
            <input value={pasted} placeholder="Paste Merriam-Webster's pronunciation" disabled={busy}
              aria-label="Paste Merriam-Webster's pronunciation" onChange={(e) => paste(e.target.value)} />
          </div>
          {pasteFail && <div className="ct-check-fail">Couldn't convert this one. Type it in.</div>}
          <div className="ct-pron-row">
            <input value={draft} placeholder="BLAN-dish-munt" disabled={busy} aria-label="Pronunciation"
              onChange={(e) => { setDraft(e.target.value); setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim() && !busy) void approve(); }} />
            <button className="ct-approve" disabled={!draft.trim() || busy} onClick={approve}>{busy ? 'Saving...' : 'Approve'}</button>
            <button className="ct-secondary" disabled={busy} onClick={() => { setOpen(false); setError(null); }}>Close</button>
          </div>
          {error && <div className="ct-check-fail">{error}</div>}
        </div>
      )}
    </div>
  );
}

type ArpabetSource = 'cmu' | 'respelling' | 'edit';
type WordAudio =
  | { available: false }
  | {
    available: true; status: 'none' | 'draft' | 'approved' | 'stale'; key: string | null; url: string | null;
    made_from: string | null; take_arpabet: string | null; arpabet: string | null; arpabet_source: ArpabetSource | null;
    suggested_arpabet: string | null; suggested_source: ArpabetSource | null; in_cmu: boolean;
    model: string | null; made_at: string | null; approved_at: string | null; can_make: boolean;
  };

const AUDIO_STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'waiting for you to listen', cls: 'ct-s-generated' },
  approved: { label: 'approved', cls: 'ct-s-approved' },
  stale: { label: 'pronunciation changed: make it again', cls: 'ct-s-needs_fixes' },
};
const ARPABET_SOURCE: Record<ArpabetSource, string> = {
  cmu: 'from the CMU dictionary (agrees with the approved pronunciation)',
  respelling: 'converted from the approved pronunciation',
  edit: 'typed by you',
};

/**
 * Word audio (2026-10-30, Odin's ruling "Word audio"; method P, Odin 2026-10-30):
 * Make audio sends the approved pronunciation's phoneme spelling (CMU Arpabet,
 * sent as IPA on eleven_v4 since the 2026-10-30 voice switch; the worker does
 * the conversion) to Mister Lexicon's voice. The spelling
 * sits beside the player in an edit box: change it and Make again to fix one.
 * Approve opens once the take has played to the end, so nothing is approved
 * unheard. Only approved audio reaches games.audio_url on Publish.
 */
function AudioLine({ page, onChanged }: { page: WordPage; onChanged: () => void }) {
  const a = page.audio;
  const ready = !!a && a.available;
  const [busy, setBusy] = useState<'make' | 'approve' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [heard, setHeard] = useState<string | null>(null);
  const [arpabet, setArpabet] = useState(ready ? (a.arpabet || '') : '');
  if (!a) return null;
  if (!a.available) {
    return <div className="ct-difficulty">Audio: <span className="ct-empty">not set up on the database yet</span></div>;
  }
  const status = AUDIO_STATUS[a.status] || null;
  const edited = arpabet.trim() !== (a.arpabet || '');
  const make = async () => {
    setBusy('make'); setError(null);
    try {
      await api.post(`/api/content/${encodeURIComponent(page.id)}/audio`, { arpabet: arpabet.trim() });
      onChanged();
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };
  const approve = async () => {
    setBusy('approve'); setError(null);
    try {
      await api.post(`/api/content/${encodeURIComponent(page.id)}/audio/approve`, { key: a.key });
      onChanged();
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };
  if (!a.can_make && a.status === 'none') {
    return <div className="ct-difficulty">Audio: <span className="ct-empty">waits on an approved pronunciation</span></div>;
  }
  const source = edited ? 'not made yet' : (a.arpabet_source ? ARPABET_SOURCE[a.arpabet_source] : null);
  return (
    <div className="ct-difficulty ct-pron">
      Audio: {status ? <span className={`ct-status ${status.cls}`}>{status.label}</span> : <span className="ct-empty">none</span>}
      {a.status !== 'approved' && <span className="ct-hint"> · Publish writes no audio until it is approved</span>}
      <div className="ct-pron-row">
        {a.url && <audio className="ct-audio" controls preload="none" src={a.url} onEnded={() => setHeard(a.key)} />}
        {a.can_make && (
          <input className="ct-arpabet" value={arpabet} placeholder="B AE0 D AH0 N AA1 ZH" disabled={!!busy}
            aria-label="Phoneme spelling" title="CMU Arpabet: every vowel has a stress number (1 stressed, 0 not); words separated by |"
            onChange={(e) => { setArpabet(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy) void make(); }} />
        )}
        {a.status === 'draft' && !edited && (
          <button className="ct-approve" disabled={!!busy || heard !== a.key} onClick={approve}>
            {busy === 'approve' ? 'Saving...' : 'Approve'}
          </button>
        )}
        {a.can_make && (
          <button className={a.url ? 'ct-secondary' : 'ct-approve'} disabled={!!busy || !arpabet.trim()} onClick={make}>
            {busy === 'make' ? 'Making...' : a.url ? 'Make again' : 'Make audio'}
          </button>
        )}
      </div>
      <div className="ct-hint">
        Phoneme spelling{source ? `: ${source}` : ''}{a.made_from ? ` · take made for ${a.made_from}` : ''}
        {a.status === 'draft' && !edited && heard !== a.key ? ' · play it to the end, then Approve' : ''}
        {edited ? ' · press Make again to hear it' : ''}
      </div>
      {error && <div className="ct-check-fail">{error}</div>}
    </div>
  );
}

/** Difficulty 1-5 from the Word Bank scorer, or "not scored" with Re-run (2026-10-22). */
function DifficultyLine({ page, onChanged }: { page: WordPage; onChanged: () => void }) {
  const [note, setNote] = useState<string | null>(null);
  const running = useRunStore((s) => s.runs.find((r) => r.kind === 'score' && r.wordId === page.id));
  const busy = !!running;
  const d = page.difficulty;
  const rerun = async () => {
    setNote(null);
    try {
      const r = await trackRun(
        { kind: 'score', wordId: page.id, word: page.word, label: 'Re-run scoring' },
        () => api.post(`/api/wordbank/${encodeURIComponent(page.id)}/rescore`, {}) as Promise<{ word: string; score: ScoreResult }>,
      );
      setNote(r.score.ok && !r.score.stopped_at ? null : scoreText(r.word, r.score));
      onChanged();
    } catch (e) { setNote(errorText(e)); }
  };
  if (d && d.score !== null) {
    return (
      <div className="ct-difficulty">
        Difficulty: <b>{d.score} of 5</b>{d.reason && <span className="ct-hint"> · {d.reason}</span>}
      </div>
    );
  }
  const why = d?.scored_at && d.vet_verdict ? ` (vetting said ${d.vet_verdict})` : '';
  return (
    <div className="ct-difficulty">
      Difficulty: <span className="ct-empty">not scored{why}</span>{' '}
      <button className={`ct-link ${running ? 'run-pulse' : ''}`} disabled={busy} onClick={rerun}>
        {running ? <>Scoring… <RunTimer startedAt={running.startedAt} /></> : 'Re-run'}
      </button>
      {busy && <span className="ct-hint"> vetting and scoring can take up to a minute</span>}
      {note && <div className="ct-check-fail">{note}</div>}
    </div>
  );
}

function SensePicker({ page, onChanged, onClose }: { page: WordPage; onChanged: () => void; onClose: () => void }) {
  const [info, setInfo] = useState<SenseInfo | null>(null);
  const [custom, setCustom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let off = false;
    setInfo(null); setError(null);
    api.get(`/api/content/${encodeURIComponent(page.id)}/senses`)
      .then((d) => { if (!off) setInfo(d as SenseInfo); })
      .catch((e) => { if (!off) setError(errorText(e)); });
    return () => { off = true; };
  }, [page.id]);

  const choose = async (sense: string) => {
    setBusy(true); setError(null);
    try {
      await api.put(`/api/content/${encodeURIComponent(page.id)}/sense`, { sense });
      setCustom('');
      onChanged();
      onClose();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="ct-sense">
      {page.vet_definition && (
        <div className="ct-sense-row">
          <button disabled={busy} onClick={() => choose('')}>Use this</button>
          <span className="ct-pos">vetted</span> {page.vet_definition}
        </div>
      )}
      {!info && !error && <div className="ct-empty">Looking up senses...</div>}
      {info && info.senses.length > 0 && (
        <ul className="ct-sense-list">
          {info.senses.map((s, i) => (
            <li key={i}>
              <button disabled={busy} onClick={() => choose(s.text)}>Use this</button>
              <span className="ct-pos">{s.part_of_speech || ''}</span> {s.text}
            </li>
          ))}
        </ul>
      )}
      {info && info.senses.length === 0 && (
        <div className="ct-check-note">
          No dictionary senses found{info.lookup_error ? ` (${info.lookup_error})` : ''}.
        </div>
      )}
      <div className="ct-sense-custom">
        <input value={custom} placeholder="Or type the sense" onChange={(e) => setCustom(e.target.value)} />
        <button disabled={busy || !custom.trim()} onClick={() => choose(custom)}>Set sense</button>
        <button className="ct-link" onClick={onClose}>Close</button>
      </div>
      {error && <div className="ct-check-fail">{error}</div>}
    </div>
  );
}

interface PublishResult {
  word: string;
  game_date: string;
  game_number: number;
  action: 'insert' | 'overwrite';
  related: { slug: string; action: string }[];
  copied_from: string;
  unsourced: string[];
  published_by: string | null;
  published_at: string;
  row: (Record<string, unknown> & { game_content: Record<string, unknown> }) | null;
}

// Publish (2026-10-04): an approved Game Word goes to the live games row for
// one date, on Odin's click only. Nothing automatic, nothing scheduled. The
// read-back is the row as the database holds it after the write.
const PUBLISH_COLUMNS = [
  'game_date', 'game_number', 'word', 'word_slug', 'phonetics', 'part_of_speech', 'audio_url', 'etymology',
  'definition_r', 'definition_w1', 'definition_w2', 'definition_w3', 'winner_reframe', 'loser_reframe',
  'reveal_line_1', 'reveal_line_2', 'reveal_line_3', 'fake_tag_1', 'fake_tag_2', 'fake_tag_3', 'publish_source',
];

function PublishPanel({ page }: { page: WordPage }) {
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<PublishResult | null>(null);

  const publish = async () => {
    if (!date) return;
    if (!window.confirm(`Publish "${page.word}" to ${date}?\n\nThis writes the live games row for ${date}. Publishing the same word to the same date again overwrites that row.`)) return;
    setBusy(true); setError(null); setDone(null);
    try {
      setDone(await api.post(`/api/content/${encodeURIComponent(page.id)}/publish`, { game_date: date }) as PublishResult);
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const shown = (v: unknown) => (v === null || v === undefined || v === '' ? <span className="ct-empty">(empty)</span> : String(v));

  return (
    <div className="ct-publish">
      <div className="ct-actions">
        <label className="ct-hint" htmlFor="ct-publish-date">Game date</label>
        <input id="ct-publish-date" className="ct-publish-date" type="date" value={date} disabled={busy}
          onChange={(e) => { setDate(e.target.value); setDone(null); setError(null); }} />
        <button className="ct-primary" disabled={!date || busy} onClick={publish}>
          {busy ? 'Publishing...' : 'Publish'}
        </button>
      </div>
      {error && <div className="ct-check-fail">{error}</div>}
      {done && (
        <div className="ct-result">
          {done.action === 'overwrite' ? 'Overwrote' : 'Published'} "{done.word}" as game #{done.game_number} on {done.game_date}.
          Copy, ads and site config copied from {done.copied_from}.
        </div>
      )}
      {done?.row && (
        <div className="ct-fields">
          <div className="ct-field-head"><span className="ct-field-name">Read back from the games table</span></div>
          {PUBLISH_COLUMNS.map((c) => (
            <div key={c} className="ct-publish-row"><span className="ct-pos">{c}</span> {shown(done.row![c])}</div>
          ))}
          <div className="ct-field-head"><span className="ct-field-name">game_content</span></div>
          {Object.entries(done.row.game_content).map(([k, v]) => (
            <div key={k} className="ct-publish-row"><span className="ct-pos">{k}</span> {shown(v)}</div>
          ))}
          <div className="ct-check-note">No source on the Content page, so not filled by Publish: {done.unsourced.join(', ')}.</div>
        </div>
      )}
      <PublishedGames page={page} reloadKey={done?.published_at || ''} />
    </div>
  );
}

interface PublishedGame {
  game_date: string;
  game_number: number | null;
  excused: number;
  excused_at: string | null;
  excused_by: string | null;
}

interface ExcuseResult {
  game_date: string;
  players_recalculated: number;
  savers_refunded: number;
  savers_spent: number;
}

// Excuse this day (2026-10-21): a day that failed on our side never costs a
// player a streak or an Excused Absence. Confirmed inline, once per date.
function PublishedGames({ page, reloadKey }: { page: WordPage; reloadKey: string }) {
  const [games, setGames] = useState<PublishedGame[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ExcuseResult | null>(null);
  const base = `/api/content/${encodeURIComponent(page.id)}`;

  const load = useCallback(async () => {
    try {
      setGames(((await api.get(`${base}/games`)) as { games: PublishedGame[] }).games);
    } catch (e) { setError(errorText(e)); }
  }, [base]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const excuse = async (date: string) => {
    setBusy(true); setError(null); setDone(null);
    try {
      setDone(await api.post(`${base}/excuse-day`, { game_date: date }) as ExcuseResult);
      setConfirming(null);
      await load();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };

  if (!games || games.length === 0) return error ? <div className="ct-check-fail">{error}</div> : null;
  return (
    <div className="ct-fields ct-published">
      <div className="ct-field-head"><span className="ct-field-name">Published games</span></div>
      {games.map((g) => (
        <div key={g.game_date} className="ct-publish-row">
          <span className="ct-pos">{g.game_date}</span> game #{g.game_number ?? '?'}{' '}
          {g.excused ? (
            <span className="ct-hint">Excused{g.excused_by ? ` by ${g.excused_by}` : ''}{g.excused_at ? ` on ${g.excused_at.slice(0, 10)}` : ''}. Nobody loses a streak or an Excused Absence to this day.</span>
          ) : confirming === g.game_date ? (
            <span className="ct-actions">
              <span className="ct-hint">Excuse {g.game_date}? Players who missed it get their streaks back and any Excused Absence spent on it; a held Excused Absence covers any other day they missed in that gap. This can't be undone.</span>
              <button className="ct-primary" disabled={busy} onClick={() => excuse(g.game_date)}>{busy ? 'Excusing...' : 'Yes, excuse this day'}</button>
              <button disabled={busy} onClick={() => setConfirming(null)}>Cancel</button>
            </span>
          ) : (
            <button disabled={busy} onClick={() => { setConfirming(g.game_date); setDone(null); setError(null); }}>Excuse this day</button>
          )}
        </div>
      ))}
      {error && <div className="ct-check-fail">{error}</div>}
      {done && (
        <div className="ct-result">
          Excused {done.game_date}. Streaks recalculated for {done.players_recalculated} player{done.players_recalculated === 1 ? '' : 's'}; {done.savers_refunded} Excused Absence{done.savers_refunded === 1 ? '' : 's'} refunded
          {done.savers_spent > 0 ? `; ${done.savers_spent} held Excused Absence${done.savers_spent === 1 ? '' : 's'} spent to keep a streak` : ''}.
        </div>
      )}
    </div>
  );
}

function StageStrip({ stages, current, running, onPick }: {
  stages: StagePanel[]; current: string; running: Set<string>; onPick: (id: string) => void;
}) {
  return (
    <div className="ct-strip" role="tablist" aria-label="Stages">
      {stages.map((st) => (
        <button
          key={st.id}
          role="tab"
          aria-selected={st.id === current}
          className={`ct-strip-step ct-strip-${st.status} ${st.id === current ? 'active' : ''}`}
          onClick={() => onPick(st.id)}
          title={`${stageTitle(st.id)}: ${running.has(st.id) ? 'generating' : STAGE_MARK[st.status]}`}
        >
          <span className="ct-strip-id">{running.has(st.id) && <Spinner label="Generating" />}{stageTitle(st.id)}</span>
          <span className="ct-strip-mark">{STAGE_MARK[st.status]}</span>
        </button>
      ))}
    </div>
  );
}

function WordView({ id, onOpen, onListChanged }: {
  id: string; onOpen: (id: string, stage?: string | null) => void; onListChanged: () => void;
}) {
  const wantStage = useUiStore((s) => s.contentStage);
  const [page, setPage] = useState<WordPage | null>(null);
  const [stageId, setStageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'approve-stage' | 'unapprove-stage' | 'approve' | null>(null);
  // Model runs on this word, from the run store, so they survive leaving and reopening the page.
  const runs = useRunStore((s) => s.runs);
  const finished = useRunStore((s) => s.finished[id]);
  const runningStages = new Set(runs.filter((r) => r.wordId === id && r.stage).map((r) => r.stage!));
  const [result, setResult] = useState<string | null>(null);
  const [senseOpen, setSenseOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = await api.get(`/api/content/${encodeURIComponent(id)}`) as WordPage;
      setPage(p);
      setError(null);
      return p;
    } catch (e) { setError(errorText(e)); return null; }
  }, [id]);

  useEffect(() => {
    setPage(null); setResult(null); setSenseOpen(false); setStageId(null);
    load().then((p) => {
      if (!p) return;
      const pick = (wantStage && p.stages.find((s) => s.id === wantStage)) || p.stages.find((s) => s.status !== 'approved') || p.stages[p.stages.length - 1];
      setStageId(pick ? pick.id : null);
    });
  }, [load, wantStage, onOpen]);

  const refresh = () => { load(); onListChanged(); };
  // The open stage's Your direction boxes with an unsaved change, by kind.
  const drafts = useRef(new Map<DirectionKind, DraftSave>());
  const directionDrafts = useMemo(() => ({
    register: (kind: DirectionKind, save: DraftSave) => {
      drafts.current.set(kind, save);
      return () => { if (drafts.current.get(kind) === save) drafts.current.delete(kind); };
    },
    saveAll: () => saveDrafts(drafts.current.values()),
  }), []);
  // A run on this word ended (here or after the page was reopened): show what it wrote.
  useEffect(() => {
    if (finished) { load(); onListChanged(); }
  }, [finished, load, onListChanged]);
  const url = (tail: string) => `/api/content/${encodeURIComponent(id)}${tail}`;

  // The page reloads through the run store's finished mark, not here.
  const generate = async (sid: string) => {
    if (!page) return;
    setResult(null); setError(null);
    try {
      // An unsaved direction is saved first; a failed save shows in its box and nothing runs.
      if (!(await directionDrafts.saveAll())) return;
      const r = await trackRun(
        { kind: 'generate', wordId: id, word: page.word, stage: sid, label: stageName(sid) },
        () => api.post(url(`/stage/${sid}/generate`), {}) as Promise<{
          refused?: Record<string, string>; candidates_added?: string[]; refused_candidates?: Record<string, string>;
        } & JudgeResult>,
      );
      if (r.candidates_added) {
        const bad = Object.keys(r.refused_candidates || {}).length;
        const added = `${r.candidates_added.length} fakes added as candidates${bad ? `, ${bad} marked needs fixes by the Doctrine checker` : ''}.`;
        setResult(r.judge?.pre_ticked
          ? `${added} The judge rated them and pre-ticked its top three. Change any pick before you approve.`
          : `${added} ${r.judge_error || 'Pick three.'}`);
        return;
      }
      // The Doctrine line is not part of this message: it is read live from
      // the page below, so it clears once a field is fixed (2026-10-02).
      setResult(`${stageName(sid)} generated.`);
    } catch (e) { setError(errorText(e)); }
  };
  const approveStage = async (sid: string) => {
    setBusy('approve-stage'); setError(null); setResult(null);
    try {
      const r = (await api.post(url(`/stage/${sid}/approve`), {})) as {
        judge_agreement?: { agreed: number; picks: number }; judge_record_error?: string;
      } | null;
      const p = await load();
      onListChanged();
      const next = p?.stages[p.stages.findIndex((s) => s.id === sid) + 1];
      // Stage 1b: how many of Odin's picks the judge also had (2026-10-22).
      const judged = r?.judge_agreement
        ? ` The judge agreed on ${r.judge_agreement.agreed} of ${r.judge_agreement.picks}.`
        : r?.judge_record_error ? ` ${r.judge_record_error}` : '';
      setResult(`${stageName(sid)} approved.${judged}${next ? ` ${stageName(next.id)} can be generated now.` : ' Every stage is approved; Approve content is ready.'}`);
      if (next) setStageId(next.id);
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };
  const unapproveStage = async (sid: string) => {
    setBusy('unapprove-stage'); setError(null); setResult(null);
    try {
      const r = (await api.post(url(`/stage/${sid}/unapprove`), {})) as { content_unapproved?: boolean } | null;
      await load();
      onListChanged();
      setResult(`${stageName(sid)} unapproved. Generate again, Regenerate and Edit work on it now.${r?.content_unapproved ? ' The content approval was cleared.' : ''}`);
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };
  const approve = async () => {
    setBusy('approve'); setError(null);
    try {
      await api.post(url('/approve'), {});
      setResult('Content approved.');
      refresh();
    } catch (e) { setError(errorText(e)); } finally { setBusy(null); }
  };

  if (!page) {
    return (
      <div className="ct-word">
        {error ? <div className="ct-check-fail">{error}</div> : <div className="ct-empty">Loading...</div>}
      </div>
    );
  }

  const locked = !!page.content_approved_at;
  const stage = page.stages.find((s) => s.id === stageId) || null;
  const stageHasContent = stage ? stage.fields.some((f) => f.value) : false;
  const stageRun = stage ? runs.find((r) => r.kind === 'generate' && r.wordId === id && r.stage === stage.id) : undefined;
  // Any run still going on this stage: its Generate, a field's Regenerate, and
  // on Stage 1b (and Reveal lines, which follow the picked fakes) a fake's
  // Regenerate, Add another fake or Judge again. Approve waits for them all,
  // so nothing is approved mid-change (2026-10-22).
  const fakeRun = (r: Run) => r.kind === 'fake' || !!r.field?.startsWith('fake:') || r.field === 'fake-judge';
  const stageBusy = !!stage && runs.some((r) => r.wordId === id && (
    r.stage === stage.id
    || (!!r.field && stage.fields.some((f) => f.field === r.field))
    || ((stage.id === '1b' || stage.id === 'rv') && fakeRun(r))));
  const wordBusy = runs.some((r) => r.wordId === id);
  // Approve content locks every stage but the Hook, which is social only (2026-10-21).
  const contentLocked = locked && stage?.id !== 'hk';
  const stageLocked = contentLocked || !stage || stage.status === 'approved';
  const fakesPicked = stage?.candidates ? stage.candidates.filter((c) => c.selected).length : 3;
  // A field that failed the Doctrine check keeps its text, marked needs fixes;
  // Approve stays off until every such field passes (2026-10-02).
  const stageNeedsFixes = stage?.status === 'needs_fixes';
  // Fields failing the Doctrine check right now, from the page itself, so the
  // line follows every Edit and Regenerate instead of the last Generate.
  const failingNow = !stage ? [] : stage.fields.filter((f) => f.check?.blocking).map((f) => FIELD_LABEL[f.field] || f.field);
  const senseFrom = page.sense.source === 'chosen' ? 'chosen' : page.sense.source === 'vetted' ? 'vetted definition' : 'no sense on record';

  return (
    <div className="ct-word">
      <div className="ct-word-head">
        <h2>{page.word}</h2>
        <span className="ct-type">{page.type === 'game' ? 'Game Word' : 'Social only word'}</span>
        <StatusChip status={page.status} />
      </div>
      <DifficultyLine page={page} onChanged={refresh} />
      <PronunciationLine key={`${page.id}:${page.pronunciation && page.pronunciation.available ? `${page.pronunciation.phonetics}:${page.pronunciation.status}` : ''}`}
        page={page} onChanged={refresh} />
      <AudioLine key={`${page.id}:${page.audio && page.audio.available ? `${page.audio.key}:${page.audio.status}:${page.audio.arpabet}` : ''}`}
        page={page} onChanged={refresh} />

      {page.cost && page.cost.calls > 0 && (
        <div className="ct-cost-line" title="From the cost ledger. Cached = input read from the prompt cache; written = input written to it.">
          Cost: <b>{costText(page.cost)}</b> over {page.cost.calls} call{page.cost.calls === 1 ? '' : 's'}
          {page.cost.recent[0] && <span className="ct-hint"> · last call {costText(page.cost.recent[0])}</span>}
          {page.cost.by_stage && page.cost.by_stage.length > 0 && (
            <div className="ct-cost-stages">
              By stage:{' '}
              {costGroups(page.cost.by_stage).map((g, gi) => (
                <span key={g.label}>
                  {gi > 0 && ' · '}
                  {g.label}:{' '}
                  {g.rows.map((s, i) => (
                    <span key={`${s.stage_id}-${s.other_word ? 'rel' : 'own'}`}>
                      {i > 0 && ', '}
                      {s.stage_id}{s.other_word ? ' (another word)' : PROMPT_NOTE[s.stage_id] ? ` ${PROMPT_NOTE[s.stage_id]}` : ''} <b>${s.usd.toFixed(4)}</b>
                      <span className="ct-hint"> ({s.calls})</span>
                    </span>
                  ))}
                </span>
              ))}
              {' '}· running total <b>${page.cost.usd.toFixed(4)}</b>
            </div>
          )}
        </div>
      )}

      <div className="ct-sense-line">
        Sense: <b>{page.sense.text}</b> <span className="ct-hint">({senseFrom})</span>{' '}
        {!locked && !senseOpen && <button className="ct-link" onClick={() => setSenseOpen(true)}>change sense</button>}
      </div>
      {senseOpen && !locked && <SensePicker page={page} onChanged={refresh} onClose={() => setSenseOpen(false)} />}

      <StageStrip stages={page.stages} current={stageId || ''} running={runningStages} onPick={(sid) => { setStageId(sid); setResult(null); setError(null); }} />

      {stage && (
        <DirectionDrafts.Provider value={directionDrafts}>
        <div className="ct-stage">
          <div className="ct-stage-head">
            <h3>{stageTitle(stage.id)}</h3>
            <span className={`ct-strip-mark ct-mark-${stage.status}`}>{STAGE_MARK[stage.status]}</span>
            {(STAGE_DIRECTIONS[stage.id] || []).some((k) => page.directions?.[k]) && <span className="ct-direction-set">Direction set</span>}
          </div>
          {(stage.last_generated_at || stage.approved_at) && (
            <div className="ct-hint">
              {[stage.last_generated_at && `Last generated ${centralTime(stage.last_generated_at)}`,
                stage.approved_at && `Approved ${centralTime(stage.approved_at)}`].filter(Boolean).join(' · ')}
            </div>
          )}
          {(STAGE_DIRECTIONS[stage.id] || []).map((kind) => <YourDirection key={kind} page={page} kind={kind} onSaved={load} />)}
          <div className="ct-actions">
            <button className={`ct-primary ${stageRun ? 'run-pulse' : ''}`} disabled={!stage.can_generate || !!busy || !!stageRun} onClick={() => generate(stage.id)}
              title={stageRun ? `Generating ${stageName(stage.id)}` : stage.generate_blocked || `Generate ${stageName(stage.id)}`}>
              {stageRun ? <>Generating... <RunTimer startedAt={stageRun.startedAt} /></> : stageHasContent ? 'Generate again' : 'Generate'}
            </button>
            <button className="ct-approve" disabled={stageLocked || !stageHasContent || !!busy || stageBusy || fakesPicked !== 3 || stageNeedsFixes}
              title={stageBusy ? 'A run on this stage is still going. Approve when it ends.'
                : stageNeedsFixes ? 'A field needs fixes. Edit or regenerate it until it passes the Doctrine check.' : undefined}
              onClick={() => approveStage(stage.id)}>
              {busy === 'approve-stage' ? 'Approving...' : stage.status === 'approved' ? `${stageName(stage.id)} approved` : `Approve ${stageName(stage.id)}`}
            </button>
            {stage.status === 'approved' && (
              <button className="ct-secondary" disabled={!!busy} onClick={() => unapproveStage(stage.id)}
                title={contentLocked ? `Back to generated. This also clears the word's content approval.` : 'Back to generated. Later stages keep their content and status.'}>
                {busy === 'unapprove-stage' ? 'Unapproving...' : `Unapprove ${stageName(stage.id)}`}
              </button>
            )}
            {stage.generate_blocked && !contentLocked && stage.status !== 'approved' && <span className="ct-hint">{stage.generate_blocked}</span>}
          </div>
          {result && <div className="ct-result">{result}</div>}
          {stageHasContent && (failingNow.length
            ? <div className="ct-check-fail">Needs fixes (Doctrine checker): {failingNow.join(', ')}. Edit or regenerate them to approve the stage.</div>
            : <div className="ct-check-note">No field is failing the Doctrine checker.</div>)}
          {error && <div className="ct-check-fail">{error}</div>}

          {stage.id === '1b' && <FakeReference page={page} />}

          {stage.id === 'rv' && (
            <RevealLines wordId={page.id} word={page.word} stage={stage} locked={locked || !stageHasContent} busy={stageBusy} onSaved={refresh} />
          )}

          {stage.id !== '1b' && stage.id !== 'rv' && (
            <div className="ct-fields">
              {stage.fields.map((f) => (
                <Fragment key={f.field}>
                  {f.field === 'paa_q1' && (
                    <div className="ct-group-head">People Also Ask <span className="ct-group-note">checked, never blocks approval</span></div>
                  )}
                  <FieldEditor wordId={page.id} word={page.word} row={f} locked={contentLocked || !stageHasContent} onSaved={refresh} history
                    movable moveBusy={stageBusy} />
                  {/^fun_fact_[123]$/.test(f.field) && (
                    <FactUrlBox wordId={page.id} field={f.field} url={page.fact_urls?.[f.field] ?? null}
                      locked={!stageHasContent} onSaved={refresh} />
                  )}
                </Fragment>
              ))}
            </div>
          )}

          {stage.id === '1b' && stage.candidates && (stage.candidates.length > 0 || stage.can_generate || stage.status !== 'empty') && (
            <FakeCandidates wordId={page.id} word={page.word} candidates={stage.candidates} fields={stage.fields} locked={locked} onChanged={refresh}
              judge={stage.judge} picksBy={stage.picks_by} />
          )}
        </div>
        </DirectionDrafts.Provider>
      )}

      <div className="ct-final">
        <button className="ct-approve" disabled={locked || !page.all_stages_approved || !!busy || wordBusy} onClick={approve}
          title={wordBusy ? 'A run on this word is still going. Approve when it ends.' : page.all_stages_approved ? '' : 'Approve every stage first'}>
          {busy === 'approve' ? 'Approving...' : locked ? 'Content approved' : 'Approve content'}
        </button>
        {!locked && !page.all_stages_approved && <span className="ct-hint">Approve every stage to enable this.</span>}
      </div>
      {page.type === 'game' && locked && <PublishPanel page={page} />}
    </div>
  );
}

export default function ContentPage() {
  const [audit, setAudit] = useState(false);
  const openId = useUiStore((s) => s.contentOpenId);
  const openWord = useUiStore((s) => s.openContentWord);
  const fetchBinder = useBinderStore((s) => s.fetchBinder);
  const onOpen = useCallback((id: string, stage?: string | null) => openWord(id, stage ?? null), [openWord]);

  useEffect(() => { if (openId) setAudit(false); }, [openId]);

  return (
    <div className="content-page">
      <div className="ct-header">
        <div className="ct-tabs">
          <button className={!audit ? 'active' : ''} onClick={() => setAudit(false)}>Word</button>
          <button className={audit ? 'active' : ''} onClick={() => setAudit(true)}>Content Audit</button>
        </div>
      </div>
      {audit && <ContentAuditView />}
      {!audit && !openId && (
        <div className="ct-pick">
          <h1>Content</h1>
          <p className="ct-sub">
            Pick a word in the Word Binder on the left. Content is made one stage at a time, in the old pipeline order:
            Generate, review each field, Approve stage, then the next stage unlocks. A Game Word has stages 1 Meaning, 2 Fakes, 3 Reveals,
            4 Assignment, 5 Examples, 6 Win/Lose (Reveals unlock once three fakes are picked, the Assignment once Meaning is approved;
            Related Words are no longer made for a Game Word). A Social only word runs the same stages, for social posts only: it never goes on the calendar or publishes.
          </p>
        </div>
      )}
      {!audit && openId && <WordView id={openId} onOpen={onOpen} onListChanged={fetchBinder} />}
    </div>
  );
}
